// ─── STORY HISTORY — the snapshot and the live window ──────────────────────
//
// RP keeps a story in two places at once. `fullEmbeddedMsgsRef` is the whole
// story as it stood when loadSession last ran, and `messages` is the rendered
// window — the last RP_PAGE_SIZE (100) beats, because a 900-message story
// locked up the UI. Only the window receives the turn that just happened;
// nothing in the send path writes back to the snapshot.
//
// Nicole, 2026-08-12: "sometimes it gives the same response as last response
// (or just responding to an earlier response) no matter how much I try to
// regenerate it and I have to keep sending my response sometimes 3x to get a
// new one (I can't delete the old responses or else it'll stay stuck in
// loop)".
//
// The prompt builder used to choose between the two by length — snapshot wins
// if longer — which on any story past 100 messages meant the snapshot ALWAYS
// won and the newest message was dropped from the conversation array on every
// turn. The model was handed a transcript ending one turn back and asked to
// continue it, so it reproduced its own previous reply.

/**
 * Splice a paged window back onto the full-story snapshot.
 *
 * PREFIX SEMANTICS. The window decides where the transcript ENDS — everything
 * returned before it comes from the snapshot, and nothing from the snapshot is
 * ever appended after it. This is the difference that matters for regenerate,
 * which passes a prefix (`live.slice(0, msgIndex)`): a set-difference merge
 * would re-inject the rejected reply and every message after it, handing the
 * model the exact text it was asked to replace.
 *
 * @param {Array}    full  snapshot of the whole story, chronological
 * @param {Array}    win   the live window, chronological, ending at "now"
 * @param {Function} fp    identity for a message (id, or text+speaker)
 * @returns {Array}        full history ending exactly where `win` ends
 */
export function mergeStoryHistory(full, win, fp) {
  const w = Array.isArray(win) ? win : [];
  if (!Array.isArray(full) || full.length === 0 || w.length === 0) return w;

  const firstFp = fp(w[0]);
  const at = full.findIndex(m => fp(m) === firstFp);

  // at === 0: the window already starts at the beginning of the story, so
  // there is no older prefix to add. Nothing is lost.
  if (at === 0) return w;

  // at === -1: the window is not part of this snapshot (a new story, or ids
  // drifted). The window stands alone — losing old context costs depth, while
  // guessing costs the current turn, and the current turn is what was broken.
  //
  // SAID OUT LOUD rather than swallowed. This is a real degradation: on a long
  // story it drops everything before the rendered page from the prompt, which
  // is precisely the memory loss v244 set out to fix. A silent version of this
  // is how the original bug survived three days and two investigations.
  if (at < 0) {
    if (full.length > w.length) {
      console.warn(
        `[storyHistory] window not found in the ${full.length}-message snapshot — ` +
        `the prompt falls back to the ${w.length} rendered messages and loses the ` +
        `older story. Message identity has drifted between the two lists.`
      );
    }
    return w;
  }

  const have = new Set(w.map(fp));
  const older = full.slice(0, at).filter(m => !have.has(fp(m)));
  return older.length ? [...older, ...w] : w;
}
